/* =============================================================
   Chesto Sticky Add-to-Cart — behavior
   -------------------------------------------------------------
   • Show/hide on scroll (IntersectionObserver sentinel)
   • Live countdown to cutoff hour
   • AJAX add-to-cart via /cart/add.js
   • Drawer / bottom-sheet open/close (scrim, esc, X)
   • Quantity stepper inside drawer
   • Re-render on variant change (variant:change CustomEvent)
   • Optional handoff to theme's own cart drawer
   ============================================================= */

(function () {
  'use strict';

  if (customElements.get('chesto-sticky-atc')) return;

  const pad = (n) => String(n).padStart(2, '0');
  const formatMoney = (cents) => {
    // Use Shopify's window.Shopify.formatMoney if available, otherwise a
    // sensible NL default ("€449,–").
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.shop_money_format) {
      return window.Shopify.formatMoney(cents, window.shop_money_format);
    }
    const euros = Math.floor(cents / 100);
    const remainder = cents % 100;
    return remainder === 0
      ? `€${euros},–`
      : `€${euros},${pad(remainder)}`;
  };

  class ChestoStickyATC extends HTMLElement {
    connectedCallback() {
      // STAP 1: Verberg de Shopify section-wrapper ONMIDDELLIJK — voorkomt dat
      // de wrapper een lege/zichtbare bar achterlaat onder de footer terwijl
      // de rest van init nog draait. Bovendien: als chesto-reveal.liquid een
      // transform op .shopify-section zet, breekt dat fixed positioning van
      // descendant kinderen. Wrapper hiden lost beide issues op.
      const wrapper = this.closest('.shopify-section');
      if (wrapper) {
        wrapper.style.display = 'none';
        wrapper.style.position = 'absolute';
        wrapper.style.width = '0';
        wrapper.style.height = '0';
        wrapper.style.overflow = 'hidden';
        wrapper.style.pointerEvents = 'none';
      }

      // Defer init until children are parsed. When the HTML parser upgrades
      // a custom element, connectedCallback fires BEFORE child nodes are
      // inserted, so querySelectorAll on descendants returns 0 results.
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this._init(), { once: true });
      } else if (this.querySelector('[data-add-button]')) {
        this._init();
      } else {
        // Children not yet parsed — wait one microtask + frame
        requestAnimationFrame(() => this._init());
      }
    }

    _init() {
      if (this._inited) return;
      this._inited = true;

      // Dedupe: als er meerdere chesto-sticky-atc op de pagina staan (bv. product
      // page heeft 'm in product.json + theme.liquid rendert 'm ook globaal),
      // bewaar alleen de eerste — die heeft de juiste product-context.
      const all = document.querySelectorAll('chesto-sticky-atc');
      if (all.length > 1 && all[0] !== this) {
        // Ook de wrapper hiden voor het verwijderde element
        const dupWrapper = this.closest('.shopify-section');
        if (dupWrapper) dupWrapper.style.display = 'none';
        this.remove();
        return;
      }

      // Portal to body to escape any parent's transform/will-change which would
      // break our position:fixed children. Without this, the mobile pill can
      // render at document-bottom instead of viewport-bottom on iOS Safari.
      // Note: de wrapper-section blijft achter maar is al display:none via
      // connectedCallback hierboven — geen visuele impact.
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
      }

      try {
        this.productId   = this.dataset.productId;
        this.variantId   = this.dataset.variantId;
        this.cartUrl     = this.dataset.cartUrl || '/cart';
        this.cutoffHour  = parseInt(this.dataset.cutoffHour || '20', 10);
        this.showAfter   = parseInt(this.dataset.showAfter || '400', 10);
        this.handoff     = true; // Chesto: always use theme cart-drawer (chc-cart)

        this.btnAdd       = this.querySelectorAll('[data-add-button]');
        this.priceNodes   = this.querySelectorAll('[data-price]');
        this.countdownEls = this.querySelectorAll('[data-countdown]');
        this.drawer       = this.querySelector('[data-drawer]');
        this.scrim        = this.querySelector('[data-scrim]');
        this.drawerBody   = this.querySelector('[data-drawer-body]');
        this.subtotalNode = this.querySelector('[data-subtotal]');
        this.drawerSummary = this.querySelector('[data-drawer-summary]');

        this.removeAttribute('hidden');

        this._wireScroll();
        this._wireCountdown();
        this._wireAdd();
        this._wireDrawer();
        this._wireQty();
        this._wireBundleSwap();
        this._wireUpsell();
        this._wireInsured();
        this._wireDiscount();
        this._wireVariantChanges();
        this._wireExternalShow();
        this._wireGlobalCartIcon();
        this._wireGlobalAddForms();
      } catch (err) {
        console.error('[chesto-sticky-atc] init error:', err);
      }
    }

    /* ── upsell ("Vaak bij gekocht") add-to-cart ── */
    _wireUpsell() {
      const btn = this.querySelector('[data-upsell-add]');
      if (!btn || btn._upBound) return;
      btn._upBound = true;
      this._syncUpsellFromCart();
      btn.addEventListener('click', async () => {
        const variantId = btn.dataset.upsellVariantId;
        if (!variantId) return;
        btn.setAttribute('aria-busy', 'true');
        const isAdded = btn.classList.contains('is-added');
        try {
          if (!isAdded) {
            const fd = new FormData();
            fd.append('id', variantId);
            fd.append('quantity', '1');
            const res = await fetch('/cart/add.js', { method: 'POST', body: fd });
            if (!res.ok) throw new Error('Upsell add failed: ' + res.status);
          } else {
            const cart = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(r => r.json());
            const line = (cart.items || []).find((i) => String(i.variant_id) === String(variantId));
            if (line) {
              const updates = {};
              updates[line.key] = 0;
              await fetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({ updates })
              });
            }
          }
          await this._refreshCart();
        } catch (err) {
          console.error('[chesto-sticky-atc] upsell toggle failed:', err);
        } finally {
          btn.removeAttribute('aria-busy');
        }
      });
    }

    _syncUpsellFromCart() {
      const btn = this.querySelector('[data-upsell-add]');
      if (!btn) return;
      const variantId = btn.dataset.upsellVariantId;
      if (!variantId) return;
      fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          const inCart = (cart.items || []).some((i) => String(i.variant_id) === String(variantId));
          btn.classList.toggle('is-added', inCart);
          btn.setAttribute('aria-checked', String(inCart));
        })
        .catch(() => {});
    }

    /* ── verzekerde bezorging: toggle voegt extra variant toe aan cart ── */
    _wireInsured() {
      const toggle = this.querySelector('[data-insured-toggle]');
      const wrap = this.querySelector('[data-insured]');
      if (!toggle || toggle._inBound) return;
      toggle._inBound = true;

      // Sync checked state from current cart
      this._syncInsuredFromCart();

      toggle.addEventListener('change', async () => {
        const variantId = toggle.dataset.insuredVariantId;
        const checked = toggle.checked;
        if (wrap) wrap.setAttribute('aria-busy', 'true');
        toggle.disabled = true;
        try {
          if (checked) {
            const fd = new FormData();
            fd.append('id', variantId);
            fd.append('quantity', '1');
            await fetch('/cart/add.js', { method: 'POST', body: fd });
          } else {
            const cart = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(r => r.json());
            const line = (cart.items || []).find((i) => String(i.variant_id) === String(variantId));
            if (line) {
              await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({ id: line.key, quantity: 0 }),
              });
            }
          }
          await this._refreshCart();
        } catch (err) {
          console.error('[chesto-sticky-atc] insured toggle failed:', err);
          toggle.checked = !checked;
        } finally {
          toggle.disabled = false;
          if (wrap) wrap.removeAttribute('aria-busy');
        }
      });
    }

    async _syncInsuredFromCart() {
      const toggle = this.querySelector('[data-insured-toggle]');
      if (!toggle) return;
      try {
        const cart = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(r => r.json());
        const variantId = toggle.dataset.insuredVariantId;
        toggle.checked = (cart.items || []).some((i) => String(i.variant_id) === String(variantId));
      } catch (_) {}
    }

    /* ── kortingscode: opslaan + bij checkout meesturen ── */
    _wireDiscount() {
      const toggle  = this.querySelector('[data-discount-toggle]');
      const form    = this.querySelector('[data-discount-form]');
      const input   = this.querySelector('[data-discount-input]');
      const apply   = this.querySelector('[data-discount-apply]');
      const applied = this.querySelector('[data-discount-applied]');
      const codeOut = this.querySelector('[data-discount-code]');
      const remove  = this.querySelector('[data-discount-remove]');
      if (!toggle || !form || !apply) return;

      // Restore previously-applied code
      let saved = null;
      try { saved = localStorage.getItem('chestoDiscountCode'); } catch (_) {}
      if (saved) this._setDiscountApplied(saved);

      toggle.addEventListener('click', () => {
        form.hidden = !form.hidden;
        if (!form.hidden && input) input.focus();
      });

      const submitCode = () => {
        const code = (input.value || '').trim().toUpperCase();
        if (!code) return;
        try { localStorage.setItem('chestoDiscountCode', code); } catch (_) {}
        this._setDiscountApplied(code);
      };

      apply.addEventListener('click', submitCode);
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); submitCode(); }
      });

      if (remove) {
        remove.addEventListener('click', () => {
          try { localStorage.removeItem('chestoDiscountCode'); } catch (_) {}
          applied.hidden = true;
          toggle.hidden = false;
          if (input) input.value = '';
          const ck = this.querySelector('[data-checkout-link]');
          if (ck) ck.href = '/checkout';
        });
      }
    }

    _setDiscountApplied(code) {
      const toggle  = this.querySelector('[data-discount-toggle]');
      const form    = this.querySelector('[data-discount-form]');
      const applied = this.querySelector('[data-discount-applied]');
      const codeOut = this.querySelector('[data-discount-code]');
      if (codeOut) codeOut.textContent = code;
      if (form) form.hidden = true;
      if (toggle) toggle.hidden = true;
      if (applied) applied.hidden = false;
      const ck = this.querySelector('[data-checkout-link]');
      if (ck) ck.href = '/checkout?discount=' + encodeURIComponent(code);
    }

    /* ── globaal: cart-icoon in header opent Chesto cart-drawer (chc-cart) ── */
    _wireGlobalCartIcon() {
      if (document._catcCartIconBound) return;
      document._catcCartIconBound = true;
      const self = this;
      document.addEventListener('click', async function (e) {
        const icon = e.target.closest('#cart-icon-bubble, a[href="/cart"]:not([data-no-drawer-intercept]), a[href$="/cart"]:not([data-no-drawer-intercept]), [data-chp-cart-trigger]');
        if (!icon) return;
        // Niet onderscheppen als de klik uit de cart-drawer zelf komt
        if (icon.closest('cart-drawer') || icon.closest('.chesto-atc__drawer')) return;
        const drawer = document.querySelector('cart-drawer');
        if (!drawer || typeof drawer.open !== 'function') return;
        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();
        // Refresh cart-drawer sectie zodat inhoud actueel is, open dan
        try {
          const r = await fetch('/?section_id=cart-drawer');
          const html = await r.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const src = doc.querySelector('cart-drawer .drawer__inner');
          const dst = drawer.querySelector('.drawer__inner');
          if (src && dst) {
            dst.innerHTML = src.innerHTML;
            const isEmpty = !doc.querySelector('cart-drawer .chc-cart__items');
            drawer.classList.toggle('is-empty', isEmpty);
          }
        } catch (_) {}
        drawer.open();
      }, true);
    }

    /* ── globaal: ALLE add-to-cart formulieren openen onze drawer ── */
    _wireGlobalAddForms() {
      if (document._catcGlobalSubmitBound) return;
      document._catcGlobalSubmitBound = true;

      document.addEventListener('submit', async (e) => {
        const form = e.target;
        if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;
        // Sla het product-hero form over (heeft eigen handler in ph-js.liquid)
        if (form.classList.contains('ph-form')) return;
        // Onderschep alleen als chesto-sticky-atc op de pagina staat
        if (!document.querySelector('chesto-sticky-atc')) return;

        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();
        try {
          const fd = new FormData(form);
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Accept': 'application/javascript' },
            body: fd,
          });
          if (!res.ok) throw new Error('Add failed: ' + res.status);
          const item = await res.json();
          const variantId = fd.get('id') || (item && item.variant_id);
          document.dispatchEvent(new CustomEvent('chesto-atc:show', {
            detail: { item, variantId }
          }));
        } catch (err) {
          console.error('[chesto-sticky-atc] global add failed:', err);
          // Fallback: laat form normaal submitten
          form.submit();
        }
      }, true); // capture phase — vóór Dawn's product-form.js
    }

    /* ── external trigger: open Chesto cart-drawer after a cart add elsewhere ── */
    _wireExternalShow() {
      document.addEventListener('chesto-atc:show', async (e) => {
        const first = document.querySelector('chesto-sticky-atc');
        if (first && first !== this) return;
        if (!this.isConnected) return;
        this._tryHandoffToTheme();
      });
    }

    /* ── visibility on scroll ─────────────────────────────── */
    _wireScroll() {
      // Sticky bar verschijnt alleen wanneer de hoofd CTA BOVEN de viewport
      // is gepasseerd (user heeft naar beneden gescrolld). NIET wanneer de CTA
      // initieel nog onder de viewport zit (op kleine schermen).
      const mainCta = document.querySelector('[data-ph-add]');
      if (mainCta) {
        const check = () => {
          const rect = mainCta.getBoundingClientRect();
          // rect.bottom < 0  →  CTA is volledig BOVEN viewport (gepasseerd)
          if (rect.bottom < 0) this.classList.add('is-visible');
          else this.classList.remove('is-visible');
        };
        check();
        window.addEventListener('scroll', check, { passive: true });
        window.addEventListener('resize', check, { passive: true });
        return;
      }
      // Fallback: scroll-px based (geen CTA gevonden, bv. niet-product pagina)
      const reveal = () => {
        if (window.scrollY > this.showAfter) this.classList.add('is-visible');
        else this.classList.remove('is-visible');
      };
      reveal();
      window.addEventListener('scroll', reveal, { passive: true });
    }

    /* ── live countdown to cutoff hour ──────────────────────
       Rolling 24h: zodra de cutoff bereikt is, springt 'ie meteen
       naar +24h. Nooit zichtbaar op 00:00:00.
       Weekend (zat/zon): aftelpil blijft staan, alleen "morgen in huis"
       wordt vervangen door "maandag in huis" (zondag is geen bezorgdag). */
    _wireCountdown() {
      if (!this.countdownEls.length) return;
      const DAY_MS = 24 * 60 * 60 * 1000;
      const tick = () => {
        const now = new Date();
        const day = now.getDay();
        const isWeekend = (day === 0 || day === 6);

        // Swap "morgen in huis" → "maandag in huis" (or back) on the parent label
        this.countdownEls.forEach((el) => {
          const parent = el.parentNode;
          if (!parent) return;
          parent.childNodes.forEach((n) => {
            if (n.nodeType !== Node.TEXT_NODE) return;
            const raw = n.nodeValue;
            if (!raw) return;
            if (isWeekend && raw.indexOf('morgen in huis') !== -1) {
              n.nodeValue = raw.replace('morgen in huis', 'maandag in huis');
            } else if (!isWeekend && raw.indexOf('maandag in huis') !== -1) {
              n.nodeValue = raw.replace('maandag in huis', 'morgen in huis');
            }
          });
        });

        const t = new Date(now);
        t.setHours(this.cutoffHour, 0, 0, 0);
        if (t.getTime() <= now.getTime()) t.setDate(t.getDate() + 1);
        let diff = t.getTime() - now.getTime();
        if (diff <= 0) diff = DAY_MS;
        const h = Math.floor(diff / 3600000);
        const m = Math.floor((diff / 60000) % 60);
        const s = Math.floor((diff / 1000) % 60);
        const text = `${pad(h)}:${pad(m)}:${pad(s)}`;
        this.countdownEls.forEach((el) => { el.textContent = text; });
      };
      tick();
      this._countdownTimer = setInterval(tick, 1000);
    }

    disconnectedCallback() {
      if (this._countdownTimer) clearInterval(this._countdownTimer);
    }

    /* ── add-to-cart ──────────────────────────────────────── */
    _wireAdd() {
      this.btnAdd.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this._addToCart(btn);
        });
      });
    }

    async _addToCart(btn) {
      if (!this.variantId) return;
      this._setButtonState(btn, 'loading');

      const formData = new FormData();
      formData.append('id', this.variantId);
      formData.append('quantity', '1');

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/javascript' },
          body: formData,
        });
        if (!res.ok) throw new Error('Add failed: ' + res.status);
        const item = await res.json();

        this._setButtonState(btn, 'added');
        setTimeout(() => this._setButtonState(btn, 'idle'), 1500);

        // Broadcast for theme listeners
        document.dispatchEvent(new CustomEvent('cart:added', { detail: { item } }));
        document.dispatchEvent(new CustomEvent('cart:refresh'));

        if (this.handoff && this._tryHandoffToTheme()) return;

        await this._refreshCart();
        this._openDrawer();
      } catch (err) {
        console.error('[chesto-sticky-atc] add to cart failed:', err);
        this._setButtonState(btn, 'idle');
        // Fall back: send the user to the cart page so they don't lose the action.
        // (Theme might have a banner; we don't want to silently swallow.)
        if (err && /sold out|unavailable/i.test(err.message || '')) {
          alert('Helaas — dit product is op dit moment niet leverbaar.');
        }
      }
    }

    _setButtonState(btn, state) {
      const dflt = btn.querySelector('.chesto-atc__btn-default');
      const done = btn.querySelector('.chesto-atc__btn-added');
      if (!dflt || !done) return;
      if (state === 'added') {
        dflt.hidden = true; done.hidden = false;
        btn.style.transform = 'scale(0.98)';
        setTimeout(() => { btn.style.transform = ''; }, 140);
      } else if (state === 'loading') {
        btn.setAttribute('aria-busy', 'true');
      } else {
        dflt.hidden = false; done.hidden = true;
        btn.removeAttribute('aria-busy');
      }
    }

    /* ── theme drawer handoff ─────────────────────────────── */
    _tryHandoffToTheme() {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.open !== 'function') return false;
      // Refresh cart-drawer section before opening so the just-added item shows.
      fetch('/?section_id=cart-drawer')
        .then(function (r) { return r.text(); })
        .then(function (html) {
          try {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const src = doc.querySelector('cart-drawer .drawer__inner');
            const dst = drawer.querySelector('.drawer__inner');
            if (src && dst) {
              dst.innerHTML = src.innerHTML;
              drawer.classList.remove('is-empty');
            }
          } catch (e) {}
          // Also refresh the cart-icon-bubble in the header
          fetch('/?section_id=cart-icon-bubble')
            .then(function (r) { return r.text(); })
            .then(function (h) {
              try {
                const d = new DOMParser().parseFromString(h, 'text/html');
                const bubble = document.getElementById('cart-icon-bubble');
                const nb = d.getElementById('cart-icon-bubble');
                if (bubble && nb) bubble.innerHTML = nb.innerHTML;
              } catch (e) {}
              // Update Chesto header count bubble too
              try {
                const countNode = document.querySelector('[data-chp-cart-count]');
                if (countNode) {
                  fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) {
                    if (c && typeof c.item_count === 'number') {
                      countNode.textContent = c.item_count;
                      if (c.item_count > 0) countNode.removeAttribute('hidden');
                      else countNode.setAttribute('hidden', '');
                    }
                  });
                }
              } catch (e) {}
            })
            .finally(function () { drawer.open(); });
        })
        .catch(function () { drawer.open(); });
      return true;
    }

    /* ── re-render cart in own drawer ─────────────────────── */
    async _refreshCart() {
      try {
        const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
        if (!res.ok) return;
        const cart = await res.json();
        this._renderCart(cart);
        this._updateCartIconBubble();
      } catch (e) {
        // No-op: drawer's pre-rendered line will remain.
      }
    }

    /* ── update Dawn's cart-icon-bubble via Section Rendering API ── */
    async _updateCartIconBubble() {
      try {
        const res = await fetch('/?sections=cart-icon-bubble');
        if (!res.ok) return;
        const data = await res.json();
        let html = data && data['cart-icon-bubble'];
        if (!html) return;
        // Strip the shopify-section wrapper if present — anders krijgt het bolletje
        // een extra nesting laag waardoor zijn position:absolute uit het lood loopt
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const wrapper = tmp.querySelector('[id^="shopify-section"]');
        if (wrapper) html = wrapper.innerHTML;
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble) bubble.innerHTML = html;
      } catch (e) {
        // Silently ignore — cart still works, just bubble may be stale
      }
    }

    _renderCart(cart) {
      if (!this.drawerBody) return;
      const items = cart.items || [];

      // Insured shipping + upsell-toggle variants — don't show as separate cart lines
      const insuredToggle = this.querySelector('[data-insured-toggle]');
      const insuredVariantId = insuredToggle ? String(insuredToggle.dataset.insuredVariantId) : null;
      const upsellToggleBtn = this.querySelector('[data-upsell-toggle]');
      const upsellToggleVariantId = upsellToggleBtn ? String(upsellToggleBtn.dataset.upsellVariantId) : null;
      const istToggleBtn = this.querySelector('[data-ist-toggle]');
      const istToggleVariantId = istToggleBtn ? String(istToggleBtn.dataset.istVariantId) : null;
      const hiddenIds = new Set();
      if (insuredVariantId) hiddenIds.add(insuredVariantId);
      if (upsellToggleVariantId) hiddenIds.add(upsellToggleVariantId);
      if (istToggleVariantId) hiddenIds.add(istToggleVariantId);
      const visibleItems = hiddenIds.size > 0
        ? items.filter((it) => !hiddenIds.has(String(it.variant_id)))
        : items;

      // Empty state: no real products in cart → also clean up orphan hidden items
      // (verzekerde bezorging / upsell die alleen meegelift werden), zodat de
      // cart écht leeg is, het cart-icoon-bolletje verdwijnt en geen €5,99 blijft hangen.
      if (visibleItems.length === 0) {
        const orphanHiddenItems = items.filter((it) => hiddenIds.has(String(it.variant_id)));
        if (orphanHiddenItems.length > 0) {
          const updates = {};
          orphanHiddenItems.forEach((it) => { updates[it.key] = 0; });
          fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ updates })
          })
            .then(() => {
              // Cart will now be truly empty — clear opt-out so next add re-includes insured
              try { localStorage.removeItem('chesto_insured_optout'); } catch (_) {}
              return fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then((r) => r.json());
            })
            .then((freshCart) => this._renderCart(freshCart))
            .catch(() => {});
          // Don't render half-state yet — wait for the re-render after orphans cleared
          return;
        }
        // Truly empty (no hidden orphans either) — also clear opt-out
        try { localStorage.removeItem('chesto_insured_optout'); } catch (_) {}
        this.drawerBody.innerHTML =
          '<div class="chesto-atc__empty-msg">' +
            '<p class="chesto-atc__empty-title">Je winkelwagen is leeg</p>' +
            '<p class="chesto-atc__empty-sub">Sluit dit venster en blader verder door onze producten.</p>' +
          '</div>';
        if (this.drawerSummary) this.drawerSummary.textContent = '0 artikelen';
        if (this.subtotalNode) this.subtotalNode.textContent = formatMoney(cart.total_price || 0);
        // Update cart-icon bubble (verbergt het blauwe dotje bij lege cart)
        this._updateCartIconBubble();
        return;
      }

      // Preserve bundle picker, upsell, shipping note across re-renders.
      // Cache outerHTML on first sight so we can re-insert even after empty-state wipe.
      const lines = visibleItems.map((it) => this._renderLine(it)).join('');
      // Bundle picker mag alleen tonen als het bijbehorende product nog in cart zit
      const sectionProductId = this.dataset.productId;
      const productInCart = sectionProductId && visibleItems.some(
        (it) => String(it.product_id) === String(sectionProductId)
      );
      const bundleEl  = this.drawerBody.querySelector('[data-bundle-upsell]');
      const upsellEl  = this.drawerBody.querySelector('[data-upsell], [data-ist]');
      const insuredEl = this.drawerBody.querySelector('[data-insured]');
      const noteEl    = this.drawerBody.querySelector('.chesto-atc__shipping-note');
      if (bundleEl)  this._cachedBundle  = bundleEl.outerHTML;
      if (upsellEl)  this._cachedUpsell  = upsellEl.outerHTML;
      if (insuredEl) this._cachedInsured = insuredEl.outerHTML;
      if (noteEl)    this._cachedNote    = noteEl.outerHTML;
      const bundle  = productInCart ? (bundleEl ? bundleEl.outerHTML : (this._cachedBundle || '')) : '';
      const upsell  = upsellEl ? upsellEl.outerHTML : (this._cachedUpsell || '');
      const insured = insuredEl ? insuredEl.outerHTML : (this._cachedInsured || '');
      const note    = noteEl ? noteEl.outerHTML : (this._cachedNote || '');
      // Sync checkbox .checked property → HTML attribute, anders gaat de
      // toggle-state verloren bij outerHTML preservation
      if (insuredEl) {
        const inp = insuredEl.querySelector('[data-insured-toggle]');
        if (inp) {
          if (inp.checked) inp.setAttribute('checked', '');
          else inp.removeAttribute('checked');
        }
      }
      this.drawerBody.innerHTML =
        lines +
        bundle +
        upsell +
        note +
        insured;
      // Preserved outerHTML may carry stale aria-busy from the swap-in-progress
      this.drawerBody.querySelectorAll('[data-bundle-variant-id][aria-busy]').forEach(
        (b) => b.removeAttribute('aria-busy')
      );
      // Update summary + subtotal (count visible items only, not insured shipping)
      if (this.drawerSummary) {
        const totalQty = visibleItems.reduce((n, it) => n + it.quantity, 0);
        this.drawerSummary.textContent = `${totalQty} ${totalQty === 1 ? 'artikel' : 'artikelen'}`;
      }
      if (this.subtotalNode) this.subtotalNode.textContent = formatMoney(cart.total_price);
      // Sync the picker's active state to the current variant
      this._syncBundleActive();
      this._wireQty();
      this._wireBundleSwap();
      this._wireUpsell();
      this._wireInsured();
      // Ensure header cart-icon bubble reflects current cart state (bv. na qty-change)
      this._updateCartIconBubble();
    }

    /* ── bundle upsell: swap variant from inside the drawer ── */
    _wireBundleSwap() {
      this.querySelectorAll('[data-bundle-variant-id]').forEach((btn) => {
        if (btn._bsBound) return;
        btn._bsBound = true;
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this._swapToVariant(btn.dataset.bundleVariantId);
        });
      });
      this._syncBundleActive();
    }

    _syncBundleActive() {
      this.querySelectorAll('[data-bundle-variant-id]').forEach((btn) => {
        const isActive = String(btn.dataset.bundleVariantId) === String(this.variantId);
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    async _swapToVariant(newVariantId) {
      if (!newVariantId || String(newVariantId) === String(this.variantId)) return;
      this.querySelectorAll('[data-bundle-variant-id]').forEach(
        (b) => b.setAttribute('aria-busy', 'true')
      );
      // Optimistic UI: update active state immediately so the click feels instant
      const prevVariantId = this.variantId;
      this.variantId = String(newVariantId);
      this.dataset.variantId = this.variantId;
      this._syncBundleActive();

      try {
        // Get current quantity (preserve it on swap) — quick fetch
        const cartRes = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
        const cart = await cartRes.json();
        const currentLine = (cart.items || []).find(
          (i) => String(i.variant_id) === String(prevVariantId)
        );
        const qty = currentLine ? currentLine.quantity : 1;

        // Single atomic update: zero out old variant, add new with same qty
        const updates = {};
        if (currentLine) updates[prevVariantId] = 0;
        updates[newVariantId] = qty;
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ updates }),
        });

        await this._refreshCart();
      } catch (err) {
        console.error('[chesto-sticky-atc] swap variant failed:', err);
        // Roll back optimistic update
        this.variantId = String(prevVariantId);
        this.dataset.variantId = this.variantId;
        this._syncBundleActive();
      } finally {
        // Clear aria-busy on the CURRENT buttons (re-render may have replaced them)
        this.querySelectorAll('[data-bundle-variant-id]').forEach(
          (b) => b.removeAttribute('aria-busy')
        );
      }
    }

    _renderLine(it) {
      const img = it.featured_image && it.featured_image.url
        ? `<div class="chesto-atc__cart-thumb"><img src="${it.featured_image.url}" width="80" height="80" alt="" loading="lazy"></div>`
        : '';
      const variant = (it.variant_title && it.variant_title !== 'Default Title')
        ? `<div class="chesto-atc__cart-variant">${escapeHtml(it.variant_title)}</div>`
        : '';
      return `
        <div class="chesto-atc__cart-line" data-line-key="${it.key}">
          ${img}
          <div class="chesto-atc__cart-meta">
            <div class="chesto-atc__cart-title">${escapeHtml(it.product_title)}</div>
            ${variant}
            <div class="chesto-atc__cart-foot">
              <div class="chesto-atc__qty">
                <button type="button" data-qty-step="-1" aria-label="Aantal verlagen">−</button>
                <span data-qty>${it.quantity}</span>
                <button type="button" data-qty-step="1" aria-label="Aantal verhogen">+</button>
              </div>
              <div class="chesto-atc__cart-price" data-price>${formatMoney(it.final_line_price)}</div>
            </div>
          </div>
        </div>
      `;
    }

    /* ── drawer open/close ────────────────────────────────── */
    _wireDrawer() {
      if (!this.drawer) return;
      this.drawer.removeAttribute('hidden');
      if (this.scrim) this.scrim.removeAttribute('hidden');

      this.querySelectorAll('[data-close-drawer]').forEach((b) => {
        b.addEventListener('click', () => this._closeDrawer());
      });
      if (this.scrim) this.scrim.addEventListener('click', () => this._closeDrawer());
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-drawer-open')) this._closeDrawer();
      });
    }

    _openDrawer() {
      if (!this.drawer) return;
      this.classList.add('is-drawer-open');
      // Body class zodat ALLE chesto-sticky-atc instanties hun sticky bars
      // verbergen (niet alleen de canonieke). Verhindert overlay-bug bij multi-instance.
      document.body.classList.add('chesto-drawer-open');
      // Lock body scroll
      this._prevOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
    }

    _closeDrawer() {
      this.classList.remove('is-drawer-open');
      document.body.classList.remove('chesto-drawer-open');
      document.documentElement.style.overflow = this._prevOverflow || '';
    }

    /* ── qty stepper ──────────────────────────────────────── */
    _wireQty() {
      this.querySelectorAll('[data-qty-step]').forEach((btn) => {
        if (btn._chestoBound) return;
        btn._chestoBound = true;
        btn.addEventListener('click', async () => {
          const line = btn.closest('[data-line-key]');
          if (!line) {
            // Pre-rendered line, no key — just update the visible number.
            const qNode = btn.parentElement.querySelector('[data-qty]');
            const step  = parseInt(btn.dataset.qtyStep, 10);
            const next  = Math.max(1, parseInt(qNode.textContent, 10) + step);
            qNode.textContent = next;
            return;
          }
          const step = parseInt(btn.dataset.qtyStep, 10);
          const qNode = line.querySelector('[data-qty]');
          const current = parseInt(qNode.textContent, 10);
          const next = Math.max(0, current + step);
          try {
            const res = await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ id: line.dataset.lineKey, quantity: next }),
            });
            const cart = await res.json();
            this._renderCart(cart);
          } catch (e) {
            console.error('[chesto-sticky-atc] qty change failed:', e);
          }
        });
      });
    }

    /* ── react to variant change events ───────────────────── */
    _wireVariantChanges() {
      // Dawn-style: <variant-radios> / <variant-selects> dispatch a custom event
      const handler = (e) => {
        const v = e.detail && e.detail.variant;
        if (!v) return;
        this.variantId = String(v.id);
        this.dataset.variantId = this.variantId;
        // Update prices in the bar
        if (typeof v.price === 'number') {
          this.priceNodes.forEach((n) => { n.textContent = formatMoney(v.price); });
        }
        // Toggle visibility based on availability
        if (v.available === false) this.classList.add('is-unavailable');
        else this.classList.remove('is-unavailable');
      };
      // Catch the common event names — themes vary.
      ['variant:change', 'variantChange', 'product:variant-change'].forEach((name) => {
        document.addEventListener(name, handler);
      });
    }
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

  customElements.define('chesto-sticky-atc', ChestoStickyATC);

  /* ─────────────────────────────────────────────────────────────────────
     PERMANENTE DEDUPE-WATCHER — MutationObserver houdt continu de DOM in
     de gaten. Zodra er een 2e chesto-sticky-atc verschijnt (door welke
     bron dan ook: section render, late JS, BFCache, app-injection),
     wordt-ie direct verwijderd. Alleen de eerste in DOM overleeft.
     ───────────────────────────────────────────────────────────────────── */
  function killDuplicates() {
    var all = document.querySelectorAll('chesto-sticky-atc');
    if (all.length <= 1) return;
    for (var i = 1; i < all.length; i++) {
      var wrapper = all[i].closest('.shopify-section');
      if (wrapper && wrapper !== all[i]) wrapper.remove();
      else all[i].remove();
    }
  }
  function startWatcher() {
    /* Initial pass */
    killDuplicates();
    /* Continuous watch — kil duplicates zodra ze in DOM verschijnen */
    var observer = new MutationObserver(function(){
      killDuplicates();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    /* Backup: ook tijd-gebaseerde sweeps voor de eerste 3 seconden */
    setTimeout(killDuplicates, 50);
    setTimeout(killDuplicates, 200);
    setTimeout(killDuplicates, 600);
    setTimeout(killDuplicates, 1500);
    setTimeout(killDuplicates, 3000);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startWatcher, { once: true });
  } else {
    startWatcher();
  }

  /* ─────────────────────────────────────────────────────────────────────
     EXTERNAL STICKY-BAR KILLER — detecteert vreemde fixed-bottom bars
     (van apps zoals Vitals, ReConvert, etc.) die overlap geven met
     onze sticky-ATC. Verwijdert ANY fixed element op viewport-bottom
     dat "In winkelwagen"/"Add to cart" tekst bevat en NIET inside
     onze chesto-sticky-atc zit.
     ───────────────────────────────────────────────────────────────────── */
  function killExternalBars() {
    var candidates = document.body.querySelectorAll('div, section, aside, footer, form');
    candidates.forEach(function(el){
      /* Skip ons eigen element en alles erbinnen */
      if (el.tagName.toLowerCase() === 'chesto-sticky-atc' || el.closest('chesto-sticky-atc')) return;
      if (el.dataset.chestoKeep) return;
      /* Skip elements die WIJ zelf hebben gebouwd (Chesto sections) */
      if (el.closest('.section-product-shipping') || el.closest('.section-product-about')) return;
      try {
        var styles = window.getComputedStyle(el);
        if (styles.position !== 'fixed' && styles.position !== 'sticky') return;
        var rect = el.getBoundingClientRect();
        /* Onderkant van element moet in onderste 1/3 van viewport zitten */
        if (rect.bottom < window.innerHeight * 0.66) return;
        /* Element moet "In winkelwagen"-achtige tekst bevatten */
        var txt = (el.innerText || '').toLowerCase();
        if (txt.includes('in winkelwagen') || txt.includes('add to cart') || txt.includes('toevoegen')) {
          /* En het mag geen Chesto-element zijn */
          if (el.className && typeof el.className === 'string') {
            if (el.className.includes('chesto-atc') || el.className.includes('pa-') ||
                el.className.includes('puc-') || el.className.includes('ph-')) return;
          }
          console.log('[chesto-cleanup] Hidden external sticky bar:', el);
          el.style.display = 'none';
        }
      } catch(e) {}
    });
  }
  function startExternalKiller() {
    killExternalBars();
    setTimeout(killExternalBars, 200);
    setTimeout(killExternalBars, 800);
    setTimeout(killExternalBars, 2000);
    /* Continuous watch — ook na DOM-changes (apps die laat injecten) */
    var obs = new MutationObserver(function(){ killExternalBars(); });
    obs.observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startExternalKiller, { once: true });
  } else {
    startExternalKiller();
  }
})();
